import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getBookingHistory, generateInvoice } from "../services/flightBookingAPI";
import { getAirlineLogo } from "../utils/airlineImages";
import CancelBookingDrawer from "../components/CancelBookingDrawer";

import ContactSupportModal from "../components/bookingFlight/ContactSupportModal";

// Status badge colors
const getStatusBadge = (status) => {
  const statusConfig = {
    TICKET_ISSUED: { bg: "bg-green-100", text: "text-green-700", label: "Confirmed" },
    SUCCESS: { bg: "bg-green-100", text: "text-green-700", label: "Confirmed" },
    PAYMENT_SUCCESS: { bg: "bg-blue-100", text: "text-blue-700", label: "Processing" },
    PAYMENT_PENDING: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Payment Pending" },
    ORDER_CREATING: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Creating Order" },
    PAYMENT_FAILED: { bg: "bg-red-100", text: "text-red-700", label: "Payment Failed" },
    TICKET_FAILED: { bg: "bg-red-100", text: "text-red-700", label: "Booking Failed" },
    ORDER_FAILED: { bg: "bg-red-100", text: "text-red-700", label: "Order Failed" },
    CANCELLED: { bg: "bg-gray-100", text: "text-gray-700", label: "Cancelled" },
    CANCELLATION_IN_PROGRESS: { bg: "bg-amber-100", text: "text-amber-700", label: "Cancel in Progress" },
    INITIATED: { bg: "bg-gray-100", text: "text-gray-600", label: "Initiated" },
    REFUND_REQUESTED: { bg: "bg-blue-50", text: "text-blue-600", label: "Refund Requested" },
    REFUND_IN_PROGRESS: { bg: "bg-amber-50", text: "text-amber-600", label: "Refund In Progress" },
    REFUND_COMPLETED: { bg: "bg-green-50", text: "text-green-600", label: "Refund Completed" },
    REFUND_FAILED: { bg: "bg-red-50", text: "text-red-600", label: "Refund Failed" },
  };

  const config = statusConfig[status] || { bg: "bg-gray-100", text: "text-gray-600", label: status || "Unknown" };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[12px] font-bold uppercase ${config.bg} ${config.text} tracking-wide whitespace-nowrap`}>
      {config.label}
    </span>
  );
};

// Format time (e.g., 9:47 AM)
const formatTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

// Format duration
const formatDuration = (min) => {
  if (!min) return "-";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
};

// Format date
const formatDateShort = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "-";
  }
};

const BookingHistory = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportPrefillData, setSupportPrefillData] = useState({});

  const [showCancelDrawer, setShowCancelDrawer] = useState(false);
  const [selectedBookingForCancel, setSelectedBookingForCancel] = useState(null);
  const [selectedRcBookingId, setSelectedRcBookingId] = useState(null);



  const openCancelDrawer = (booking) => {
    setSelectedBookingForCancel(booking);
    setShowCancelDrawer(true);
  };

  const ITEMS_PER_PAGE = 20;

  const openSupportModal = (booking) => {
    const user = auth.currentUser;

    // Helper to parse JSON
    const parse = (data) => {
      if (!data) return {};
      if (typeof data === "object") return data;
      try {
        const first = JSON.parse(data);
        if (typeof first === 'string') return JSON.parse(first);
        return first;
      } catch { return {}; }
    };

    const ticketDetails = parse(booking.ticket_details);
    const bookingRequest = parse(booking.booking_request);
    const itinerary = ticketDetails?.FlightItinerary ||
      ticketDetails?.Response?.Response?.FlightItinerary ||
      ticketDetails?.Response?.FlightItinerary ||
      bookingRequest?.FlightItinerary ||
      bookingRequest?.Response?.Response?.FlightItinerary ||
      booking?.FlightItinerary ||
      booking?.Response?.Response?.FlightItinerary ||
      booking?.ticket_details?.FlightItinerary ||
      booking?.booking_request?.FlightItinerary;

    // Extract segments
    let segments = itinerary?.Segments || [];
    if (segments.length > 0 && Array.isArray(segments[0])) {
      segments = segments[0];
    }

    const firstSeg = segments[0] || {};
    const lastSeg = segments[segments.length - 1] || firstSeg;

    // Extract values
    const origin = firstSeg?.Origin?.Airport?.CityName || booking.source || "Origin";
    const dest = lastSeg?.Destination?.Airport?.CityName || booking.destination || "Destination";
    const pnr = booking.pnr || booking.PNR || booking.booking_pnr || itinerary?.PNR || ticketDetails?.PNR || bookingRequest?.PNR || "N/A";
    const leadPax = itinerary?.Passenger?.find(p => p.IsLeadPax) || {};

    setSupportPrefillData({
      firstName: leadPax.FirstName || '',
      lastName: leadPax.LastName || '',
      email: user?.email || leadPax.Email || '',
      phone: user?.phoneNumber?.replace("+91", "") || leadPax.ContactNo || '',
      message: `I need help with my booking from ${origin} to ${dest} (PNR: ${pnr}).`
    });
    setSelectedRcBookingId(booking.rc_booking_id);
    setShowSupportModal(true);
  };

  // Get user contact (email or phone)
  const getUserContact = useCallback(() => {
    const user = auth.currentUser;
    if (user?.email) return user.email;
    if (user?.phoneNumber) return user.phoneNumber.replace("+91", "");
    return null;
  }, []);

  // Fetch bookings
  // `currentLastKey` is passed explicitly to avoid lastKey being in the deps array,
  // which would cause fetchBookings to be recreated on every API response
  // and trigger an infinite re-fetch loop via the useEffect below.
  const fetchBookings = useCallback(async (loadMore = false, currentLastKey = null) => {
    const userContact = getUserContact();

    if (!userContact) {
      setError("Please login to view your booking history");
      setIsLoading(false);
      return;
    }

    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const result = await getBookingHistory(
        userContact,
        ITEMS_PER_PAGE,
        loadMore ? currentLastKey : null
      );

      if (result.error) {
        setError(result.error);
      }

      if (loadMore) {
        setBookings((prev) => [...prev, ...(result.bookings || [])]);
      } else {
        setBookings(result.bookings || []);
      }

      setLastKey(result.last_evaluated_key);
      setHasMore(!!result.last_evaluated_key);

    } catch (err) {
      console.error("[BookingHistory] Error:", err);
      setError(err.message || "Failed to load booking history");
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [getUserContact]); // lastKey intentionally excluded — passed as param to avoid infinite loop

  // Initial load - wait for auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchBookings(false);
      } else {
        // If we're sure the checkout is complete and user is still null, then show error
        setError("Please login to view your booking history");
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [fetchBookings]);

  // Handle browser back button to navigate to home
  useEffect(() => {
    const onBackButtonEvent = (e) => {
      e.preventDefault();
      navigate('/');
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);

    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, [navigate]);

  // Groups
  const groupedBookings = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const current = [];
    const past = [];

    bookings.forEach(booking => {
      // Filter out bookings with status "Quoted"
      if (booking.status === "Quoted" || booking.status === "QUOTED") return;

      const journeyDate = new Date(booking.journey_date || booking.created_at);
      if (journeyDate >= today) {
        current.push(booking);
      } else {
        past.push(booking);
      }
    });

    return { current, past };
  }, [bookings]);

  const handleRefresh = () => {
    setLastKey(null);
    fetchBookings(false);
  };

  return (
    <div className="min-h-[87vh] bg-white mt-16 pb-12">
      <main className="w-full md:max-w-7xl mx-auto md:border-x border-gray-100 min-h-[87vh]">
        {/* Page Title Header */}
        <div className="bg-[#FFF6EC] px-3 md:px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-transparent">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 2V5M7.5 2V5M12 2V5" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13 3.5H11C7.70017 3.5 6.05025 3.5 5.02513 4.52513C4 5.55025 4 7.20017 4 10.5V15C4 18.2998 4 19.9497 5.02513 20.9749C6.05025 22 7.70017 22 11 22H13C16.2998 22 17.9497 22 18.9749 20.9749C20 19.9497 20 18.2998 20 15V10.5C20 7.20017 20 5.55025 18.9749 4.52512C17.9497 3.5 16.2998 3.5 13 3.5Z" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8 15H12M8 11H16" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="text-[#FF4E01] font-semibold text-lg">Booking History</h1>
          <div className="flex-1" />
          <button
            onClick={handleRefresh}
            className={`p-2 hover:bg-orange-100 rounded-full transition-colors ${isLoading ? 'animate-spin' : ''}`}
          >
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Sections */}
        {isLoading && !bookings.length ? (
          <div className="flex flex-col">
            {/* Current Bookings Skeleton */}
            <div className="bg-[#1A2233] px-3 md:px-6 py-2">
              <h2 className="text-white font-medium text-sm text-center">Current Bookings</h2>
            </div>
            <div className="divide-y divide-gray-100 border-b border-gray-100">
              {[1, 2, 3, 4, 5, 6, 7].map(i => (
                <div key={i} className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center gap-4 animate-pulse">
                  {/* Airline Logo + Route */}
                  <div className="flex items-center gap-4 md:w-[25%] w-full">
                    <div className="w-10 h-10 md:w-11 md:h-11 bg-gray-200 rounded-lg" />
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="flex items-center justify-between md:w-[32%] w-full gap-4">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-5 bg-gray-200 rounded w-16" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                    <div className="flex-1 h-3 bg-gray-200 rounded" />
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-5 bg-gray-200 rounded w-16" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                  </div>

                  {/* Status - Desktop */}
                  <div className="hidden md:flex md:w-[16%] justify-center">
                    <div className="h-6 bg-gray-200 rounded-full w-24" />
                  </div>

                  {/* Price - Desktop */}
                  <div className="hidden md:flex flex-col items-end md:w-[12%] gap-2">
                    <div className="h-6 bg-gray-200 rounded w-20" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>

                  {/* Mobile Status + Price */}
                  <div className="md:hidden w-full flex items-center justify-between">
                    <div className="h-6 bg-gray-200 rounded-full w-24" />
                    <div className="flex flex-col items-end gap-2">
                      <div className="h-6 bg-gray-200 rounded w-20" />
                      <div className="h-3 bg-gray-200 rounded w-16" />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="w-full md:w-[15%] flex gap-1.5">
                    {[1, 2, 3, 4, 5].map(btn => (
                      <div key={btn} className="flex-1 md:flex-none md:w-9 h-8 bg-gray-200 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Past Bookings Skeleton */}
            <div className="bg-[#1A2233] px-3 md:px-6 py-2 mt-4">
              <h2 className="text-white font-medium text-sm text-center">Past Bookings</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center gap-4 animate-pulse">
                  {/* Same structure as above */}
                  <div className="flex items-center gap-4 md:w-[25%] w-full">
                    <div className="w-10 h-10 md:w-11 md:h-11 bg-gray-200 rounded-lg" />
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between md:w-[32%] w-full gap-4">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-5 bg-gray-200 rounded w-16" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                    <div className="flex-1 h-3 bg-gray-200 rounded" />
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-5 bg-gray-200 rounded w-16" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                  </div>
                  <div className="hidden md:flex md:w-[16%] justify-center">
                    <div className="h-6 bg-gray-200 rounded-full w-24" />
                  </div>
                  <div className="hidden md:flex flex-col items-end md:w-[12%] gap-2">
                    <div className="h-6 bg-gray-200 rounded w-20" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                  <div className="md:hidden w-full flex items-center justify-between">
                    <div className="h-6 bg-gray-200 rounded-full w-24" />
                    <div className="flex flex-col items-end gap-2">
                      <div className="h-6 bg-gray-200 rounded w-20" />
                      <div className="h-3 bg-gray-200 rounded w-16" />
                    </div>
                  </div>
                  <div className="w-full md:w-[15%] flex gap-1.5">
                    {[1, 2, 3, 4, 5].map(btn => (
                      <div key={btn} className="flex-1 md:flex-none md:w-9 h-8 bg-gray-200 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {/* Current Bookings */}
            <div className="bg-[#1A2233] px-3 md:px-6 py-2">
              <h2 className="text-white font-medium text-sm text-center">Current Bookings</h2>
            </div>
            <div className="divide-y divide-gray-100 border-b border-gray-100">
              {groupedBookings.current.length > 0 ? (
                groupedBookings.current.map((booking, idx) => (
                  <BookingRow
                    key={booking.rc_booking_id || idx}
                    booking={booking}
                    onHelpClick={() => openSupportModal(booking)}
                    onCancelClick={() => openCancelDrawer(booking)}
                  />
                ))
              ) : (
                <div className="px-3 md:px-6 py-8 text-center text-gray-400 text-sm italic">
                  No current bookings found.
                </div>
              )}
            </div>

            {/* Past Bookings */}
            <div className="bg-[#1A2233] px-3 md:px-6 py-2 mt-4">
              <h2 className="text-white font-medium text-sm text-center">Past Bookings</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {/* {console.log(groupedBookings)} */}
              {groupedBookings.past.length > 0 ? (
                groupedBookings.past.map((booking, idx) => (
                  <BookingRow
                    key={booking.rc_booking_id || idx}
                    booking={booking}
                    onHelpClick={() => openSupportModal(booking)}
                    onCancelClick={() => openCancelDrawer(booking)}
                  />
                ))
              ) : (
                <div className="px-3 md:px-6 py-8 text-center text-gray-400 text-sm italic">
                  No past bookings found.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center py-8">
            <button
              onClick={() => fetchBookings(true, lastKey)}
              disabled={loadingMore}
              className="px-6 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-50"
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}

        {!isLoading && bookings.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <p>Ready for your next adventure?</p>
            <Link to="/chat" className="mt-4 text-orange-500 font-medium">Search Flights →</Link>
          </div>
        )}

        {/* {error && (
          <div className="px-3 md:px-6 text-center text-red-500 text-sm">
            {error}
          </div>
        )} */}
      </main>

      <CancelBookingDrawer
        isOpen={showCancelDrawer}
        onClose={() => setShowCancelDrawer(false)}
        booking={selectedBookingForCancel}
        onSuccess={handleRefresh}
      />

      <ContactSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        bookingId={selectedRcBookingId}
        prefillData={supportPrefillData}
      />

    </div>
  );
};

const BookingRow = ({ booking, onHelpClick, onCancelClick }) => {
  const navigate = useNavigate();
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);

  // Parse JSON data safely
  const parseData = (data) => {
    if (!data) return {};
    if (typeof data === "object") return data;
    try {
      const first = JSON.parse(data);
      if (typeof first === "string") return JSON.parse(first); // Handle double stringified
      return first;
    } catch {
      return {};
    }
  };

  const bookingRequest = parseData(booking.booking_request);
  const ticketDetails = parseData(booking.ticket_details);
  // console.log(ticketDetails)
  const itinerary = ticketDetails?.FlightItinerary ||
    ticketDetails?.Response?.Response?.FlightItinerary ||
    ticketDetails?.Response?.FlightItinerary ||
    bookingRequest?.FlightItinerary ||
    bookingRequest?.Response?.Response?.FlightItinerary ||
    booking?.FlightItinerary ||
    booking?.Response?.Response?.FlightItinerary ||
    booking?.ticket_details?.FlightItinerary ||
    booking?.booking_request?.FlightItinerary;
  // Handle segments which can be nested in some TBO responses

  let segments = itinerary?.Segments || [];
  if (segments.length > 0 && Array.isArray(segments[0])) {
    segments = segments[0];
  }

  const firstSeg = segments[0] || {};
  const lastSeg = segments[segments.length - 1] || firstSeg;

  const airlineCode = firstSeg?.Airline?.AirlineCode || "AI";
  const airlineName = firstSeg?.Airline?.AirlineName || "Airline";
  const duration = firstSeg?.Duration || 0;
  const depTime = firstSeg?.Origin?.DepTime || booking.journey_date;
  const arrTime = lastSeg?.Destination?.ArrTime || booking.arrival_datetime;

  const originCity = firstSeg?.Origin?.Airport?.CityName || booking.source_name || booking.source || "Origin";
  const destCity = lastSeg?.Destination?.Airport?.CityName || booking.destination_name || booking.destination || "Destination";
  const pnr = booking.pnr || booking.PNR || booking.booking_pnr || itinerary?.PNR || ticketDetails?.PNR || bookingRequest?.PNR || "N/A";

  const numStops = segments.length > 1 ? segments.length - 1 : 0;
  const stopInfo = numStops === 0 ? "Nonstop" : `${numStops} Stop${numStops > 1 ? 's' : ''}`;
  // console.log('ticketDetails', ticketDetails)
  const displayPrice = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: booking.currency || "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(ticketDetails?.FlightItinerary?.Fare?.PublishedFare || booking.amount || 0);

  const handleAction = () => {
    if (booking.status === "TICKET_ISSUED" || booking.status === "SUCCESS") {
      navigate(`/booking-flight?rc_booking_id=${booking.rc_booking_id}`, {
        state: { bookingData: booking }
      });
    } else {
      navigate(`/book-flight?rc_booking_id=${booking.rc_booking_id}`);
    }
  };

  const handleView = () => {
    navigate(`/booking-detail?rc_booking_id=${booking.rc_booking_id}`);
  };

  const handleDownloadTicket = async () => {
    if (isGeneratingTicket) return;

    try {
      setIsGeneratingTicket(true);
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || booking?.user_id || booking?.email || "anon").trim();

      const payload = {
        ticket_details: ticketDetails,
        platform_fee: booking?.rc_platform_fee || 0,
        gst_rate: booking?.rc_gst_rate || 0,
        rc_booking_id: booking?.rc_booking_id,
        user_id: userIdentifier,
        document_type: 'ticket'
      };

      const response = await generateInvoice(payload);
      if (response.status === "success" && response.ticket_url) {
        const link = document.createElement('a');
        link.href = response.ticket_url;
        link.download = `ticket_${booking.rc_booking_id || 'download'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.message || "Failed to generate ticket");
      }
    } catch (error) {
      console.error("Ticket generation error:", error);
      alert("Failed to generate or download ticket. Please try again.");
    } finally {
      setIsGeneratingTicket(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (isGeneratingInvoice) return;

    try {
      setIsGeneratingInvoice(true);
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || booking?.user_id || booking?.email || "anon").trim();

      const payload = {
        ticket_details: ticketDetails,
        platform_fee: booking?.rc_platform_fee || 0,
        gst_rate: booking?.rc_gst_rate || 0,
        rc_booking_id: booking?.rc_booking_id,
        user_id: userIdentifier
      };

      const response = await generateInvoice(payload);
      if (response.status === "success" && response.invoice_url) {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = response.invoice_url;
        link.download = `invoice_${booking.rc_booking_id || 'download'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.message || "Failed to generate invoice");
      }
    } catch (error) {
      console.error("Invoice generation error:", error);
      alert("Failed to generate or download invoice. Please try again.");
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const isFailed = booking.status?.includes("FAILED");
  const isConfirmed = booking.status === "TICKET_ISSUED" || booking.status === "SUCCESS";
  const isCancelInProgress = booking.status === "CANCELLATION_IN_PROGRESS";
  const isCancelled = booking.status === "CANCELLED";

  return (
    <div className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center hover:bg-gray-50 transition-all border-b border-gray-50 group relative">
      {/* Col 1: Route & Airline - 25% */}
      <div className="flex items-center gap-4 md:w-[25%] w-full mb-4 md:mb-0 flex-shrink-0">
        <div className="w-10 h-10 md:w-11 md:h-11 flex-shrink-0 bg-white rounded-lg border border-gray-100 p-1.5 flex items-center justify-center shadow-sm">
          <img
            src={getAirlineLogo(airlineCode, airlineName)}
            alt={airlineName}
            className="w-full h-full object-contain"
            onError={(e) => { e.target.src = "/nologo.gif"; }}
          />
        </div>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-gray-900 font-semibold text-base uppercase tracking-tight truncate">{originCity}</span>
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-gray-900 font-semibold text-base uppercase tracking-tight truncate">{destCity}</span>
          </div>
          <span className="text-gray-400 text-[12px] md:text-[13px] font-medium leading-tight mt-1 uppercase tracking-wide truncate">
            {airlineName} • <span className="text-gray-600 font-mono font-bold">PNR: {pnr}</span>
          </span>
        </div>
      </div>

      {/* Col 2: Timeline (Times & Stops) - 32% */}
      <div className="flex items-center justify-between md:w-[32%] w-full px-4 mb-4 md:mb-0 flex-shrink-0">
        <div className="flex flex-col items-center">
          <span className="text-gray-900 font-bold text-lg tracking-tight">{formatTime(depTime)}</span>
          <span className="text-gray-400 text-[12px] uppercase font-bold tracking-widest mt-1">Departure</span>
        </div>

        <div className="flex flex-col items-center px-6 flex-1">
          <div className="flex items-center gap-1.5 w-full justify-center opacity-70">
            {segments.map((_, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-400">
                    <path d="M4.23492 2.73492C4.37222 2.59762 4.58958 2.58889 4.73687 2.70904L4.7652 2.73492L7.7652 5.73492L7.79107 5.76324C7.90323 5.90072 7.90323 6.09939 7.79107 6.23687L7.7652 6.26519L4.7652 9.26519C4.61875 9.41164 4.38137 9.41164 4.23492 9.26519C4.08848 9.11875 4.08848 8.88137 4.23492 8.73492L6.96978 6.00006L4.23492 3.2652L4.20904 3.23687C4.08889 3.08958 4.09762 2.87222 4.23492 2.73492Z" fill="currentColor" />
                  </svg>
                )}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-600">
                  <path d="M1.33325 13.3335H11.9999" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M2.55006 8.11057C2.36672 7.97724 2.20006 7.81057 2.00006 7.22724C1.94582 7.08257 1.7408 6.3573 1.56673 5.6939C1.4199 5.1343 1.28906 4.62354 1.35006 4.4939C1.40007 4.36056 1.46672 4.26056 1.68339 4.12723C1.81672 4.04518 2.50006 3.87723 2.63339 3.8439C2.76672 3.81056 2.95005 3.7939 3.10006 3.8439C3.38339 3.8939 3.96672 4.7439 4.11672 4.8439C4.18338 4.91056 4.40006 5.1045 4.65006 5.12723C4.83339 5.1439 5.01672 5.0939 5.21673 5.01056C5.40006 4.93418 9.01674 3.17723 9.35008 3.02723C12.0667 1.8939 14.0401 3.75723 14.3401 4.15723C14.6501 4.5439 14.7167 4.66056 14.6334 4.9939C14.5257 5.3439 14.2334 5.41056 14.0667 5.46056C13.9001 5.51056 11.6001 6.12723 10.7001 6.37723C10.5035 6.44124 10.4075 6.56978 10.3834 6.5939C10.2667 6.76057 9.73674 7.8939 9.58674 8.1439C9.48341 8.41057 9.20008 8.7439 8.83341 8.87724C8.45008 9.01057 7.78341 9.16057 7.63341 9.21057C7.48341 9.26057 7.13341 9.36057 7.01674 9.32724C6.86674 9.2939 6.72341 9.1439 6.79008 8.91057C6.85674 8.67724 6.98341 8.02724 7.00008 7.92724C7.01675 7.82724 7.18341 7.41057 7.00008 7.3939C6.96675 7.3439 6.61673 7.4939 6.10006 7.61057C5.71621 7.71864 5.31421 7.82557 5.03339 7.8939C3.95006 8.21057 3.36335 8.2939 3.23339 8.2939C2.98339 8.2939 2.80006 8.26057 2.55006 8.11057Z" stroke="currentColor" />
                </svg>
              </React.Fragment>
            ))}
          </div>
          <span className="text-orange-500 text-[11px] font-bold uppercase mt-1">{stopInfo}</span>
        </div>

        <div className="flex flex-col items-center">
          <span className="text-gray-900 font-bold text-lg tracking-tight">{arrTime ? formatTime(arrTime) : "--:--"}</span>
          <span className="text-gray-400 text-[12px] uppercase font-bold tracking-widest mt-1">Arrival</span>
        </div>
      </div>

      {/* Col 3: Status Badge - 16% (Hidden on mobile, shown on desktop) */}
      <div className="hidden md:flex md:w-[16%] justify-center flex-shrink-0">
        <div className="flex justify-center w-full px-2">
          {getStatusBadge((isCancelled || isFailed || isCancelInProgress) ? (booking.refund_status || booking.status) : booking.status)}
        </div>
      </div>

      {/* Col 4: Price Info - 12% (Shown after timeline on mobile, separate column on desktop) */}
      <div className="hidden md:flex flex-col items-end md:w-[12%] pr-6 flex-shrink-0">
        <span className="text-[#1A2233] font-bold text-xl leading-none tracking-tight text-right">{displayPrice}</span>
        <span className="text-gray-400 text-[11px] font-medium uppercase tracking-widest mt-1 text-right">Total Fare</span>
      </div>

      {/* Mobile: Status Badge + Price (shown after timeline) */}
      <div className="md:hidden w-full flex items-center justify-between px-4 mb-4">
        <div className="flex justify-center">
          {getStatusBadge((isCancelled || isFailed || isCancelInProgress) ? (booking.refund_status || booking.status) : booking.status)}
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[#1A2233] font-bold text-xl leading-none tracking-tight">{displayPrice}</span>
          <span className="text-gray-400 text-[11px] font-medium uppercase tracking-widest mt-1">Total Fare</span>
        </div>
      </div>

      {/* Col 5: Actions - 15% (Full width on mobile, normal on desktop) */}
      <div className="w-full md:w-[15%] flex md:justify-end flex-shrink-0">
        <div className="flex flex-wrap gap-1.5 w-full md:w-auto md:justify-end">
          <ActionButton
            icon={<DownloadIcon />}
            onClick={handleDownloadTicket}
            disabled={!isConfirmed}
            isLoading={isGeneratingTicket}
          />
          <ActionButton
            icon={<InvoiceIcon />}
            onClick={handleDownloadInvoice}
            disabled={!isConfirmed}
            isLoading={isGeneratingInvoice}
          />
          <ActionButton icon={<EyeIcon />} onClick={handleView} disabled={!isConfirmed} />

          <ActionButton icon={<XIcon />} onClick={onCancelClick} disabled={!isConfirmed || isCancelInProgress || isCancelled} />
          {/* <ActionButton icon={<RefreshIcon />} onClick={() => window.location.reload()} disabled={!isConfirmed} /> */}
          <ActionButton icon={<HelpIcon />} onClick={onHelpClick} disabled={false} />
        </div>
      </div>

      {/* Failure Insight Overlay */}
      {/* {isFailed && booking.remarks && (
        <div className="absolute inset-x-0 bottom-[-5px] py-1 bg-red-50/50 border-t border-red-100/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <span className="text-[10px] text-red-600 font-semibold truncate px-6 leading-none italic">
            Booking Insight: {booking.remarks}
          </span>
        </div>
      )} */}
    </div>
  );
};

const ActionButton = ({ icon, onClick, primary, disabled, isLoading }) => (
  <button
    onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }}
    disabled={disabled || isLoading}
    className={`flex-1 md:flex-none md:w-9 h-8 flex items-center justify-center rounded transition-all duration-200
      ${(disabled || isLoading) ? 'opacity-30 cursor-not-allowed border-gray-100' : ''}
      ${primary
        ? 'bg-[#1A2233] text-white border-[#1A2233] hover:bg-[#2a354a]'
        : 'text-gray-500 bg-white border-gray-200 hover:bg-[#1A2233] hover:text-white hover:border-[#1A2233]'
      }
    `}
    style={{ boxShadow: '0px 0px 0px 1px #D4D4D8, 0px 1px 3px #D4D4D8' }}
  >
    {isLoading ? (
      <svg className="animate-spin h-3.5 w-3.5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    ) : (
      React.cloneElement(icon, { size: 13 })
    )}
  </button>
);

// Icons updated to accept props
const DownloadIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const EditIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ExitIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const EyeIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const XIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const RefreshIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.6667 8.00016C14.6667 11.682 11.6819 14.6668 8.00004 14.6668C4.31814 14.6668 1.33337 11.682 1.33337 8.00016C1.33337 4.31826 4.31814 1.3335 8.00004 1.3335C11.6819 1.3335 14.6667 4.31826 14.6667 8.00016Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M10.3333 6.99986C10.3333 6.99986 8.61483 9.33319 7.99996 9.33319C7.38509 9.33319 5.66663 6.99986 5.66663 6.99986M7.99996 8.99986V4.6665M5.66663 11.3332H10.3333" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
);

const HelpIcon = ({ size = 30 }) => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path fill-rule="evenodd" d="M12 2a7 7 0 0 0-7 7 3 3 0 0 0-3 3v2a3 3 0 0 0 3 3h1a1 1 0 0 0 1-1V9a5 5 0 1 1 10 0v7.083A2.919 2.919 0 0 1 14.083 19H14a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h1a2 2 0 0 0 1.732-1h.351a4.917 4.917 0 0 0 4.83-4H19a3 3 0 0 0 3-3v-2a3 3 0 0 0-3-3 7 7 0 0 0-7-7Zm1.45 3.275a4 4 0 0 0-4.352.976 1 1 0 0 0 1.452 1.376 2.001 2.001 0 0 1 2.836-.067 1 1 0 1 0 1.386-1.442 4 4 0 0 0-1.321-.843Z" clip-rule="evenodd" />
  </svg>
);


const InvoiceIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

export default BookingHistory;
